import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, LOGO_SRC } from "../data/content";

const Logo = ({ onClick, compact = false }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid="nav-logo"
        className="flex items-center gap-2.5 sm:gap-3.5 focus-visible:outline-vec-green"
        aria-label="VEC — Vijayanand Engineers & Contractors, back to top"
    >
        <img
            src={LOGO_SRC}
            alt="VEC — Vijayanand Engineers & Contractors logo"
            className={`w-auto shrink-0 ${compact ? "h-9" : "h-10 sm:h-11"}`}
        />
        <span className="text-left">
            <span className="hidden md:block font-display font-semibold leading-none tracking-[0.05em] text-vec-charcoal whitespace-nowrap text-[clamp(13px,1.35vw,22px)]">
                VIJAYANAND ENGINEERS <span className="text-vec-green">&amp;</span> CONTRACTORS
            </span>
            <span className="md:hidden block font-display font-semibold text-vec-charcoal tracking-[0.04em] text-[clamp(11px,3.5vw,14px)] leading-[1.25]">
                VIJAYANAND ENGINEERS
                <span className="block text-vec-green">&amp; CONTRACTORS</span>
            </span>
        </span>
    </button>
);

export const Nav = ({ onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        onNavigate(href);
    };

    return (
        <>
            <header
                data-testid="main-nav"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-white/95 backdrop-blur-md border-b border-vec-border shadow-[0_1px_0_rgba(8,120,62,0.06),0_8px_30px_rgba(23,34,28,0.06)]"
                        : "bg-white/70 backdrop-blur-sm border-b border-transparent"
                }`}
            >
                <div className="vec-container h-[72px] flex items-center justify-between gap-6">
                    <Logo onClick={() => go("#top")} compact={scrolled} />
                    <nav className="hidden lg:flex items-center gap-4 xl:gap-7" aria-label="Primary">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.href}
                                type="button"
                                data-testid={l.testId}
                                onClick={() => go(l.href)}
                                className="font-body text-[13px] font-medium tracking-wide text-vec-charcoal/75 hover:text-vec-green transition-colors duration-300"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="nav-enquire-button"
                            onClick={() => go("#contact")}
                            className="hidden sm:inline-flex items-center gap-2 bg-vec-green hover:bg-vec-green-bright text-white font-body text-[13px] font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300"
                        >
                            Enquire Now
                            <ArrowUpRight size={15} aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            data-testid="nav-mobile-menu-button"
                            onClick={() => setOpen(true)}
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 border border-vec-border text-vec-charcoal"
                            aria-label="Open navigation menu"
                            aria-expanded={open}
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] bg-vec-charcoal text-white flex flex-col"
                    >
                        <div className="flex items-center justify-between px-4 sm:px-6 h-[72px] border-b border-white/10">
                            <span className="bg-white px-3 py-2 inline-flex">
                                <img src={LOGO_SRC} alt="VEC logo" className="h-8 w-auto" />
                            </span>
                            <button
                                type="button"
                                data-testid="mobile-menu-close-button"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center w-11 h-11 border border-white/20 text-white"
                                aria-label="Close navigation menu"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto px-6 py-10 flex flex-col gap-1" aria-label="Mobile">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.href}
                                    type="button"
                                    data-testid={`mobile-${l.testId}`}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.4 }}
                                    onClick={() => go(l.href)}
                                    className="group flex items-baseline gap-4 py-3 border-b border-white/10 text-left"
                                >
                                    <span className="font-mono-tech text-[10px] text-vec-green-bright">
                                        0{i + 1}
                                    </span>
                                    <span className="font-display text-2xl font-semibold uppercase tracking-tight group-hover:text-vec-green-bright transition-colors duration-300">
                                        {l.label}
                                    </span>
                                </motion.button>
                            ))}
                        </nav>
                        <div className="px-6 pb-8">
                            <button
                                type="button"
                                data-testid="mobile-enquire-button"
                                onClick={() => go("#contact")}
                                className="w-full inline-flex items-center justify-center gap-2 bg-vec-green hover:bg-vec-green-bright text-white font-semibold text-sm tracking-wide px-5 py-4 transition-colors duration-300"
                            >
                                Enquire Now
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
